import { ReactNode, useCallback, useMemo } from "react";
import useAuth from "../auth/useAuth";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import useRequest from "../request/useRequest";
import FeatureFlagContext from "./FeatureFlagContext";

function FeatureFlagProvider({ children }: { children: ReactNode }) {
    const { axiosRequest } = useRequest();

    const { user } = useAuth();
    const search = useSearch({
        strict: false,
    });

    const { data, isFetched } = useQuery<
        { fields: { [x: string]: { [key: string]: boolean } } } & {
            [x: string]: boolean;
        }
    >({
        queryKey: ["/dev/features"],
        enabled: !!user,
        queryFn: () =>
            axiosRequest({
                url: `dev/features`,
            }),
    });

    const features = useMemo(() => {
        return {
            ...(data ?? {}),
            ...Object.entries(search).reduce<{ [x: string]: boolean }>(
                (acc, [key]) => {
                    acc[key] = true;
                    return acc;
                },
                {},
            ),
        };
    }, [data, search]);

    const cropStagesEnabled = features["crop-stages"] ?? false;

    const newTimeEnabled = features["new-time"] ?? false;

    const notificationsV2Enabled = features["notifications-v2"] ?? false;

    const timeTravelEnabled = features["time-travel"] ?? false;

    const bulkRxEnabled = features["bulk-rx"] ?? false;

    const languagePreferenceEnabled = features["language-preference"] ?? false;

    const allNEnabled = features["all-n"] ?? false;

    const sidedressOnDemandRx = features["sidedress-on-demand-rx"] ?? false;

    const plotEditingEnabled = features["plot-editing"] ?? false;

    const deleteOperations = features["delete-operations"] ?? false;

    const virtualSIEnabled = features["virtual-si"] ?? false;

    const samplesEnabled = features["samples"] ?? false;

    const cropCyclesV2Enabled = features["crop-cycles-v2"] ?? false;

    const bulkSIEnabled = features["bulk-si"] ?? false;

    const weatherEnabled = features["weather-display"] ?? false;

    const irrigationEnabled = features["nave-irrigation"] ?? false;

    const customRecommendationsEnabled =
        features["custom-recommendations"] ?? false;

    const bulkFieldUploadEnabled = features["bulk-field-upload"] ?? false;

    const fieldConfigEnabled = features["field-config"] ?? false;

    const homeDashboardInsightsEnabled =
        features["home-dashboard-insights"] ?? false;

    const advisorEnabled = features["advisor"] ?? false;

    const soilManagementRequired = features["soil-management"] ?? false;

    const fieldIrrigationOn = useCallback(
        (fieldId: number) => {
            return (
                data?.fields?.[fieldId.toString()]?.["field-irrigation-on"] ??
                false
            );
        },
        [data],
    );

    const organizationPreviewEnabled = features["preview-tier"] ?? false;

    const organizationMonitoringEnabled = features["monitoring-tier"] ?? false;

    const networkEnabled = features["network"] ?? false;

    const fullPlotOffsetEnabled = features["full-plot-offset"] ?? false;

    return (
        <FeatureFlagContext.Provider
            value={{
                cropStagesEnabled,
                newTimeEnabled,
                notificationsV2Enabled,
                timeTravelEnabled,
                bulkRxEnabled,
                languagePreferenceEnabled,
                allNEnabled,
                sidedressOnDemandRx,
                plotEditingEnabled,
                deleteOperations,
                virtualSIEnabled,
                samplesEnabled,
                cropCyclesV2Enabled,
                bulkSIEnabled,
                weatherEnabled,
                customRecommendationsEnabled,
                bulkFieldUploadEnabled,
                fieldConfigEnabled,
                homeDashboardInsightsEnabled,
                irrigationEnabled,
                advisorEnabled,
                soilManagementRequired,
                isFetched,
                fieldIrrigationOn,
                organizationPreviewEnabled,
                organizationMonitoringEnabled,
                networkEnabled,
                fullPlotOffsetEnabled,
            }}
        >
            {children}
        </FeatureFlagContext.Provider>
    );
}

export default FeatureFlagProvider;
